import { forEach } from 'lodash-es';

import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  EmbeddedViewRef,
  Inject,
  Injectable,
  Injector,
  Optional,
  TemplateRef,
  Output,
  EventEmitter,
} from '@angular/core';

import { DomSanitizer } from '@angular/platform-browser';
import {
  ModalComponent,
  ModalEvent,
  ModalOptions,
  ModalEventAction,
} from '../component/modal/modal.component';

import { UIKit } from '../ui-kit';

/** @dynamic */
@Injectable({
  providedIn: 'root',
})
export class ModalService {
  @Output() event: EventEmitter<'open' | 'close'> = new EventEmitter();

  private modals: UIKit.Dictionary<any> = {};
  // private renderer: Renderer2;

  // read-only
  get opened(): UIKit.Dictionary<any> {
    return { ...this.modals };
  }

  constructor(
    private injector: Injector,
    private appRef: ApplicationRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private sanitizer: DomSanitizer,
    // @Optional() @Inject(DOCUMENT) private document: Document
  ) {
    // this.renderer = this.renderFactory.createRenderer(null, null);
  }

  /**
   * open modal
   */
  // tslint:disable-next-line: max-line-length
  public open(
    template: TemplateRef<unknown> | string,
    options: Partial<ModalOptions> = {},
    parent: HTMLElement = /* (this.document || document) */ document?.body,
  ): Promise<ModalEvent> {
    return new Promise((resolve, reject) => {
      const modalId = options.id
        || `modal-${(
          Date.now().toString(36) + Math.random().toString(36).substr(2, 5)
        ).toUpperCase()}`;

      try {
        //
        // this.renderer.addClass((this.document || document)?.body, 'lib-modal-open');

        const modalComponentRef = this.attachTo(ModalComponent, parent);
        this.modals[modalId] = modalComponentRef;
        //
        const instance = modalComponentRef.instance;
        instance.id = modalId;
        if (template instanceof TemplateRef) {
          instance.template = template;
        } else {
          instance.message = options.html
            ? this.sanitizer.bypassSecurityTrustHtml(template)
            : template;
        }
        instance.options = options;
        instance.event
          .subscribe((event) => {
            resolve(event);
            this.close(modalId);
          });
      } catch (error) {
        reject(error);
        this.close(modalId);
      }
      this.event.emit('open');
    });
  }

  /**
   * close modal
   */
  public close(modalId: string, modalAction: any = null): void {
    // remove component
    const toRemoveComponentRef = this.modals[modalId];
    if (toRemoveComponentRef) {
      delete this.modals[modalId];
      if (modalAction) {
        toRemoveComponentRef.instance.event.emit({ action: modalAction });
      }
      this.remove(toRemoveComponentRef);
      delete this.modals[modalId];
    }
    this.event.emit('close');
    //
    // if (Object.keys(this.modals).length <= 0) {
    //   this.renderer.removeClass((this.document || document)?.body, 'lib-modal-open');
    // }
  }

  /**
   * close all modals
   */
  public closeAll(): void {
    forEach(Object.keys(this.modals), (modalId: string) => this.close(modalId));
  }

  /**
   * is modal opened
   */
  public isOpen(id: string): boolean {
    return !!this.modals[id];
  }

  /**
   * append component to body
   */
  private attachTo(
    component: any,
    parent: HTMLElement = /* (this.document || document) */ document?.body,
  ): ComponentRef<ModalComponent> {
    // 1. Create a component reference from the component
    const componentRef = this.componentFactoryResolver
      .resolveComponentFactory(component)
      .create(this.injector);
    // 2. Attach component to the appRef so that it's inside the ng component tree
    this.appRef.attachView(componentRef.hostView);
    // 3. Get DOM element from component
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;
    // 4. Append DOM element to the body
    parent.appendChild(domElem);
    //
    return componentRef as ComponentRef<ModalComponent>;
  }

  /**
   * remove component from body
   */
  private remove(componentRef: ComponentRef<any>): void {
    // remove it from the component tree and from the DOM
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
  }
}
